import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { useSeo, SEO_KEYWORDS } from '../hooks/useSeo';

const PrivacyPolicyPage = () => {
  useSeo({ title: 'Privacy Policy | InvestiGate', description: 'How InvestiGate protects investigator, case and evidence data. Read our privacy policy.', keywords: SEO_KEYWORDS });
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center space-x-4">
          <Link to="/" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <Shield className="w-8 h-8 text-blue-600" />
          <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">
            <strong>Effective Date:</strong> June 23, 2025
          </p>

          <div className="prose prose-blue max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              At InvestiGate, we take your privacy seriously. This policy explains how we collect, use, and protect your personal information.
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Account Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you create an account, we collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Name</li>
                <li>Email address</li>
                <li>Password (encrypted)</li>
                <li>Account preferences</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Usage Data</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We automatically collect:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Pages visited and features used</li>
                <li>Time and date of access</li>
                <li>Device information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Case Data</h3>
              <p className="text-gray-700 leading-relaxed">
                Information you upload or create in the platform:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Case titles and descriptions</li>
                <li>Evidence files</li>
                <li>Activity logs</li>
                <li>Reports and analyses</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use your information to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Provide and maintain our services</li>
                <li>Process your subscription payments</li>
                <li>Send you important account updates</li>
                <li>Improve our platform and features</li>
                <li>Prevent fraud and abuse</li>
                <li>Comply with legal obligations</li>
                <li>Provide customer support</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Data Sharing & Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>We never sell your personal information.</strong> We only share data in these circumstances:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Service Providers</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use trusted third-party services:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li><strong>Stripe:</strong> Payment processing</li>
                <li><strong>MongoDB Atlas:</strong> Database hosting</li>
                <li><strong>AWS:</strong> Cloud infrastructure</li>
                <li><strong>Resend:</strong> Email delivery</li>
                <li><strong>OpenAI:</strong> AI document analysis</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Legal Requirements</h3>
              <p className="text-gray-700 leading-relaxed">
                We may disclose information if required by law, court order, or to protect our rights and safety.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement industry-standard security measures:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>SSL/TLS encryption for data in transit</li>
                <li>Encrypted password storage (bcrypt)</li>
                <li>Secure cloud infrastructure</li>
                <li>Regular security audits</li>
                <li>Access controls and authentication</li>
                <li>Automated backups</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your data for as long as your account is active or as needed to provide services. When you delete your account, we will delete or anonymize your personal information within 30 days, except where required by law to retain it longer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li><strong>Access:</strong> Request a copy of your data</li>
                <li><strong>Correct:</strong> Update inaccurate information</li>
                <li><strong>Delete:</strong> Request account and data deletion</li>
                <li><strong>Export:</strong> Download your data in portable format</li>
                <li><strong>Object:</strong> Opt-out of certain data processing</li>
                <li><strong>Withdraw Consent:</strong> Revoke permissions at any time</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, contact us at <a href="mailto:privacy@investigate.com" className="text-blue-600 hover:text-blue-700 underline">privacy@investigate.com</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies & Tracking</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar technologies for:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Authentication (keeping you logged in)</li>
                <li>Remembering preferences</li>
                <li>Analytics (understanding usage patterns)</li>
                <li>Security (preventing fraud)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You can control cookies through your browser settings, but some features may not work properly if disabled.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                InvestiGate is not intended for users under 18 years of age. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your data may be processed in countries outside your own. We ensure appropriate safeguards are in place to protect your information in accordance with this privacy policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this privacy policy from time to time. We will notify you of significant changes via email or through the platform. Your continued use after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-8 bg-blue-50 border-l-4 border-blue-600 p-6 rounded">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                For privacy-related questions or requests:
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:privacy@investigate.com" className="text-blue-600 hover:text-blue-700 underline">privacy@investigate.com</a><br />
                <strong>Support:</strong> <a href="mailto:support@investigate.com" className="text-blue-600 hover:text-blue-700 underline">support@investigate.com</a>
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>Last Updated:</strong> June 23, 2025
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-gray-900 text-gray-400 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center space-x-6 mb-4">
            <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
            <Link to="/" className="hover:text-white transition">Home</Link>
          </div>
          <p>&copy; 2025 InvestiGate. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
